import { createStore } from "zustand/vanilla";

import type { ChildProfile, ProgressRepository } from "@/domain";

import type {
  ProfileRepositoryBootstrap,
  ProfileRepositoryGeneration,
  ProfileSessionActivation,
  ProfileSessionCommands,
  ProfileSessionMachine,
  ProfileSessionNavigate,
  ProfileSessionNavigationIntent,
  ProfileSessionRoute,
  ProfileSessionSnapshot
} from "./contracts";

export type {
  ProfileSessionMachine,
  ProfileSessionNavigate,
  ProfileSessionRoute
} from "./contracts";

const inactiveNavigate: ProfileSessionNavigate = () => undefined;
const inactiveRoute: ProfileSessionRoute = {
  kind: "other",
  pathname: ""
};

function createInitialSnapshot(
  repositoryGeneration = 0
): ProfileSessionSnapshot {
  return {
    error: null,
    pendingDeepLink: null,
    persistenceMode: "local",
    profiles: [],
    repository: null,
    repositoryGeneration,
    saveError: null,
    saveStatus: "idle",
    selectedProfile: null,
    status: "loading"
  };
}

function normalizeError(error: unknown, fallbackMessage: string): Error {
  return error instanceof Error
    ? error
    : new Error(fallbackMessage, { cause: error });
}

function routesMatch(
  first: ProfileSessionRoute,
  second: ProfileSessionRoute
): boolean {
  return first.kind === second.kind && first.pathname === second.pathname;
}

function mergeCanonicalProfile(
  profiles: readonly ChildProfile[],
  canonicalProfile: ChildProfile
): readonly ChildProfile[] {
  const existingIndex = profiles.findIndex(
    (profile) => profile.id === canonicalProfile.id
  );

  if (existingIndex === -1) {
    return [...profiles, canonicalProfile];
  }

  return profiles.map((profile, index) =>
    index === existingIndex ? canonicalProfile : profile
  );
}

export function createProfileSessionMachine(): ProfileSessionMachine {
  const store = createStore<ProfileSessionSnapshot>()(() =>
    createInitialSnapshot()
  );

  let active = false;
  let navigate = inactiveNavigate;
  let currentRoute = inactiveRoute;
  let lastReportedRoute: ProfileSessionRoute | null = null;
  let acceptsColdStartDeepLink = true;
  let pendingRoute: ProfileSessionRoute | null = null;
  let hasRepositorySource = false;
  let repositorySourceGeneration: ProfileRepositoryGeneration | null = null;
  let bootstrapRepository: ProfileRepositoryBootstrap | null = null;
  let internalGeneration = 0;
  let bootstrapAttempt = 0;
  let profileRequest = 0;
  let selectionRequest = 0;
  let saveRequest = 0;
  let selectionInFlight: {
    generation: number;
    profileId: string;
    request: number;
  } | null = null;
  let saveInFlight: {
    generation: number;
    profileId: string;
    request: number;
  } | null = null;
  let deferredMapNavigation: {
    generation: number;
    request: number;
  } | null = null;

  function isCurrentGeneration(generation: number): boolean {
    return active && generation === internalGeneration;
  }

  function isBoundCommand(generation: number): boolean {
    return (
      isCurrentGeneration(generation) &&
      generation === store.getState().repositoryGeneration
    );
  }

  function isCurrentBootstrapAttempt(
    generation: number,
    attempt: number
  ): boolean {
    return isCurrentGeneration(generation) && attempt === bootstrapAttempt;
  }

  function setPendingRoute(route: ProfileSessionRoute | null): void {
    pendingRoute = route;
    if (active) {
      store.setState({
        pendingDeepLink: route?.pathname ?? null
      });
    }
  }

  function emitNavigation(
    intent: ProfileSessionNavigationIntent,
    route: ProfileSessionRoute
  ): void {
    if (!active) {
      return;
    }

    navigate(intent);
    currentRoute = route;
  }

  function showOnboardingWithHistory(
    history: "push" | "replace"
  ): void {
    if (currentRoute.kind === "onboarding") {
      return;
    }

    emitNavigation(
      {
        history,
        target: { kind: "onboarding" }
      },
      { kind: "onboarding", pathname: "" }
    );
  }

  function guardCurrentRoute(): void {
    if (store.getState().selectedProfile !== null) {
      return;
    }

    if (currentRoute.kind === "map" || currentRoute.kind === "play") {
      showOnboardingWithHistory("replace");
    }
  }

  function setRoute(route: ProfileSessionRoute): void {
    if (!active) {
      return;
    }

    if (lastReportedRoute !== null && routesMatch(lastReportedRoute, route)) {
      return;
    }

    const isColdStartRoute = acceptsColdStartDeepLink;
    if (route.kind !== "other") {
      acceptsColdStartDeepLink = false;
    }
    lastReportedRoute = route;
    currentRoute = route;

    if (
      isColdStartRoute &&
      route.kind === "play" &&
      store.getState().selectedProfile === null &&
      pendingRoute === null
    ) {
      setPendingRoute(route);
    }

    guardCurrentRoute();
  }

  function cancelProfileOperations(): void {
    profileRequest += 1;
    selectionRequest += 1;
    saveRequest += 1;
    selectionInFlight = null;
    saveInFlight = null;
    deferredMapNavigation = null;
  }

  async function loadProfiles(
    generation: number,
    repository: ProgressRepository
  ): Promise<void> {
    const request = ++profileRequest;

    if (isCurrentGeneration(generation)) {
      store.setState({
        error: null,
        status: "loading"
      });
    }

    try {
      const profiles = await repository.listProfiles();

      if (
        !isCurrentGeneration(generation) ||
        request !== profileRequest ||
        store.getState().repository !== repository
      ) {
        return;
      }

      const previousSelection = store.getState().selectedProfile;
      const canonicalSelection =
        previousSelection === null
          ? null
          : (profiles.find(
              (profile) => profile.id === previousSelection.id
            ) ?? null);

      store.setState({
        error: null,
        profiles: [...profiles],
        selectedProfile: canonicalSelection,
        status: "ready"
      });

      if (previousSelection !== null && canonicalSelection === null) {
        setPendingRoute(null);
        guardCurrentRoute();
      }
    } catch (error: unknown) {
      if (
        !isCurrentGeneration(generation) ||
        request !== profileRequest ||
        store.getState().repository !== repository
      ) {
        return;
      }

      store.setState({
        error: normalizeError(error, "Profile loading failed."),
        status: "error"
      });
    }
  }

  async function bootstrapCurrentRepository(
    generation: number,
    source: ProfileRepositoryBootstrap
  ): Promise<void> {
    const attempt = ++bootstrapAttempt;

    try {
      const repositorySession = await source();

      if (!isCurrentBootstrapAttempt(generation, attempt)) {
        return;
      }

      store.setState({
        error: null,
        persistenceMode: repositorySession.persistenceMode,
        repository: repositorySession.repository,
        status: "loading"
      });

      await loadProfiles(generation, repositorySession.repository);
    } catch (error: unknown) {
      if (!isCurrentBootstrapAttempt(generation, attempt)) {
        return;
      }

      store.setState({
        error: normalizeError(error, "Repository initialization failed."),
        status: "error"
      });
    }
  }

  function setRepositorySource(
    generation: ProfileRepositoryGeneration,
    source: ProfileRepositoryBootstrap
  ): void {
    if (!active) {
      return;
    }

    if (
      hasRepositorySource &&
      Object.is(repositorySourceGeneration, generation)
    ) {
      return;
    }

    hasRepositorySource = true;
    repositorySourceGeneration = generation;
    bootstrapRepository = source;
    internalGeneration += 1;
    cancelProfileOperations();

    store.setState({
      error: null,
      pendingDeepLink: pendingRoute?.pathname ?? null,
      persistenceMode: "local",
      profiles: [],
      repository: null,
      repositoryGeneration: internalGeneration,
      saveError: null,
      saveStatus: "idle",
      selectedProfile: null,
      status: "loading"
    });

    guardCurrentRoute();
    void bootstrapCurrentRepository(internalGeneration, source);
  }

  function applyCanonicalSelection(canonicalProfile: ChildProfile): void {
    const state = store.getState();
    const isExplicitSwitch =
      state.selectedProfile !== null &&
      state.selectedProfile.id !== canonicalProfile.id;

    if (isExplicitSwitch) {
      pendingRoute = null;
    }

    store.setState({
      pendingDeepLink: pendingRoute?.pathname ?? null,
      profiles: mergeCanonicalProfile(state.profiles, canonicalProfile),
      selectedProfile: canonicalProfile
    });
  }

  function showMap(generation: number): void {
    if (!isBoundCommand(generation)) {
      return;
    }

    if (
      selectionInFlight !== null &&
      selectionInFlight.generation === generation
    ) {
      deferredMapNavigation = { ...selectionInFlight };
      return;
    }

    if (store.getState().selectedProfile === null) {
      guardCurrentRoute();
      return;
    }

    if (pendingRoute !== null) {
      const resumedRoute = pendingRoute;
      setPendingRoute(null);
      emitNavigation(
        {
          history: "replace",
          target: {
            kind: "path",
            pathname: resumedRoute.pathname
          }
        },
        resumedRoute
      );
      return;
    }

    emitNavigation(
      {
        history: "push",
        target: { kind: "map" }
      },
      { kind: "map", pathname: "" }
    );
  }

  async function selectProfile(
    profile: ChildProfile | string,
    generation: number
  ): Promise<boolean> {
    if (!isBoundCommand(generation)) {
      return false;
    }

    const state = store.getState();
    const repository = state.repository;
    if (repository === null || state.status !== "ready") {
      return false;
    }

    const profileId = typeof profile === "string" ? profile : profile.id;
    const request = ++selectionRequest;
    selectionInFlight = null;
    deferredMapNavigation = null;
    const knownProfile = state.profiles.find(
      (candidate) => candidate.id === profileId
    );

    if (knownProfile !== undefined) {
      applyCanonicalSelection(knownProfile);
      return true;
    }

    selectionInFlight = { generation, profileId, request };

    try {
      const canonicalProfile = await repository.getProfile(profileId);

      if (
        !isBoundCommand(generation) ||
        selectionInFlight?.request !== request ||
        store.getState().repository !== repository
      ) {
        return false;
      }

      const shouldShowMap = takeDeferredMapNavigation(generation, request);
      selectionInFlight = null;

      if (
        canonicalProfile === undefined ||
        canonicalProfile.id !== profileId
      ) {
        return false;
      }

      applyCanonicalSelection(canonicalProfile);
      if (shouldShowMap) {
        showMap(generation);
      }
      return true;
    } catch {
      if (
        isBoundCommand(generation) &&
        selectionInFlight?.request === request
      ) {
        selectionInFlight = null;
        deferredMapNavigation = null;
      }
      return false;
    }
  }

  function isCurrentSave(
    generation: number,
    request: number,
    repository: ProgressRepository
  ): boolean {
    return (
      isBoundCommand(generation) &&
      request === saveRequest &&
      saveInFlight?.generation === generation &&
      saveInFlight.request === request &&
      store.getState().repository === repository
    );
  }

  function takeDeferredMapNavigation(
    generation: number,
    request: number
  ): boolean {
    const deferredNavigation = deferredMapNavigation;
    deferredMapNavigation = null;
    return (
      deferredNavigation !== null &&
      deferredNavigation.generation === generation &&
      deferredNavigation.request === request
    );
  }

  async function saveAndSelectProfile(
    profile: ChildProfile,
    generation: number
  ): Promise<boolean> {
    if (!isBoundCommand(generation)) {
      return false;
    }

    const state = store.getState();
    const repository = state.repository;
    if (repository === null || state.status !== "ready") {
      return false;
    }

    selectionRequest += 1;
    selectionInFlight = null;
    deferredMapNavigation = null;
    const request = ++saveRequest;
    saveInFlight = {
      generation,
      profileId: profile.id,
      request
    };
    store.setState({
      saveError: null,
      saveStatus: "saving"
    });

    try {
      await repository.saveProfile(profile);
      if (!isCurrentSave(generation, request, repository)) {
        return false;
      }

      const canonicalProfile = await repository.getProfile(profile.id);
      if (!isCurrentSave(generation, request, repository)) {
        return false;
      }
      if (canonicalProfile === undefined) {
        throw new Error("Saved profile could not be reloaded.");
      }

      saveInFlight = null;
      applyCanonicalSelection(canonicalProfile);
      store.setState({
        saveError: null,
        saveStatus: "idle"
      });
      showMap(generation);
      return true;
    } catch (error: unknown) {
      if (isCurrentSave(generation, request, repository)) {
        saveInFlight = null;
        store.setState({
          saveError: normalizeError(error, "Profile saving failed."),
          saveStatus: "error"
        });
      }
      return false;
    }
  }

  function retryProfiles(generation: number): void {
    if (!isBoundCommand(generation)) {
      return;
    }

    const repository = store.getState().repository;
    if (repository !== null) {
      void loadProfiles(generation, repository);
      return;
    }

    if (bootstrapRepository !== null) {
      store.setState({
        error: null,
        status: "loading"
      });
      void bootstrapCurrentRepository(generation, bootstrapRepository);
    }
  }

  function profileDeleted(profileId: string, generation: number): void {
    if (!isBoundCommand(generation)) {
      return;
    }

    const state = store.getState();
    const deletedSelection = state.selectedProfile?.id === profileId;
    const deletedTarget =
      selectionInFlight?.profileId === profileId ||
      saveInFlight?.profileId === profileId;
    cancelProfileOperations();
    if (deletedSelection || deletedTarget) {
      pendingRoute = null;
    }
    store.setState({
      error: null,
      pendingDeepLink: pendingRoute?.pathname ?? null,
      profiles: state.profiles.filter((profile) => profile.id !== profileId),
      saveError: null,
      saveStatus: "idle",
      selectedProfile: deletedSelection ? null : state.selectedProfile,
      status: state.repository === null ? state.status : "ready"
    });

    if (deletedSelection) {
      guardCurrentRoute();
    }
  }

  function allProfilesCleared(generation: number): void {
    if (!isBoundCommand(generation)) {
      return;
    }

    cancelProfileOperations();
    pendingRoute = null;
    store.setState({
      error: null,
      pendingDeepLink: null,
      profiles: [],
      saveError: null,
      saveStatus: "idle",
      selectedProfile: null,
      status: store.getState().repository === null ? "loading" : "ready"
    });
    guardCurrentRoute();
  }

  function bindCommands(
    generation = store.getState().repositoryGeneration
  ): ProfileSessionCommands {
    return {
      allProfilesCleared: () => {
        allProfilesCleared(generation);
      },
      navigation: {
        showLevel: (levelId) => {
          if (!isBoundCommand(generation)) {
            return;
          }
          if (store.getState().selectedProfile === null) {
            guardCurrentRoute();
            return;
          }
          emitNavigation(
            {
              history: "push",
              target: { kind: "level", levelId }
            },
            { kind: "play", pathname: "" }
          );
        },
        showMap: () => {
          showMap(generation);
        },
        showOnboarding: () => {
          if (!isBoundCommand(generation)) {
            return;
          }
          setPendingRoute(null);
          showOnboardingWithHistory("push");
        },
        showParentArea: () => {
          if (!isBoundCommand(generation)) {
            return;
          }
          emitNavigation(
            {
              history: "push",
              target: { kind: "parent" }
            },
            { kind: "parent", pathname: "" }
          );
        }
      },
      profileDeleted: (profileId) => {
        profileDeleted(profileId, generation);
      },
      retryProfiles: () => {
        retryProfiles(generation);
      },
      saveAndSelectProfile: (profile) =>
        saveAndSelectProfile(profile, generation),
      selectProfile: (profile) => selectProfile(profile, generation)
    };
  }

  function activate({ navigate: nextNavigate, route }: ProfileSessionActivation) {
    if (active) {
      return;
    }

    active = true;
    navigate = nextNavigate;
    currentRoute = inactiveRoute;
    lastReportedRoute = null;
    acceptsColdStartDeepLink = true;
    pendingRoute = null;
    hasRepositorySource = false;
    repositorySourceGeneration = null;
    bootstrapRepository = null;
    selectionInFlight = null;
    saveInFlight = null;
    deferredMapNavigation = null;
    store.setState(createInitialSnapshot(internalGeneration), true);
    setRoute(route);
  }

  function dispose(): void {
    if (!active) {
      return;
    }

    active = false;
    navigate = inactiveNavigate;
    hasRepositorySource = false;
    repositorySourceGeneration = null;
    bootstrapRepository = null;
    internalGeneration += 1;
    cancelProfileOperations();
    pendingRoute = null;
  }

  function setNavigate(nextNavigate: ProfileSessionNavigate): void {
    if (active) {
      navigate = nextNavigate;
    }
  }

  return {
    activate,
    bindCommands,
    dispose,
    getSnapshot: store.getState,
    setNavigate,
    setRepositorySource,
    setRoute,
    subscribe: (listener) => store.subscribe(() => listener())
  };
}
